/**
 * useMentions - Real-time mention notifications hook
 *
 * Subscribes to mentions for the current user and provides:
 * - Unread count
 * - Recent mentions
 * - Mark as read functions
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Mention, MentionStatus, NotificationSummary } from '@/types/mention.types';
import {
  subscribeMentions,
  markMentionAsRead,
  markAllMentionsAsRead,
  markEntityMentionsAsRead,
  archiveMention,
  archiveAllMentions,
  archiveReadMentions,
  unarchiveMention,
} from '@/services/mention.service';
import { getTenantIdSync } from '@/hooks/useTenantId';

interface UseMentionsOptions {
  userId: string | null;
  tenantId?: string;
  autoSubscribe?: boolean;
}

interface UseMentionsReturn {
  mentions: Mention[];
  /** Still unread — the "New" pile. */
  unreadMentions: Mention[];
  /** Read but not yet cleared — the "Earlier" pile the cascade drops into. */
  readMentions: Mention[];
  unreadCount: number;
  activeCount: number;
  loading: boolean;
  summary: NotificationSummary;
  markAsRead: (mentionId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  /** Cascade: opening a mention reads every unread tag on the same entity. */
  openMention: (mention: Mention) => Promise<void>;
  /** Cascade for surfaces reached WITHOUT clicking a mention row. */
  markEntityAsRead: (entityId: string) => Promise<void>;
  clearMention: (mentionId: string) => Promise<void>;
  clearAllMentions: () => Promise<{ id: string; priorStatus: MentionStatus }[]>;
  clearReadMentions: () => Promise<{ id: string; priorStatus: MentionStatus }[]>;
  restoreMention: (mentionId: string, priorStatus?: MentionStatus) => Promise<void>;
}

/**
 * Hook to subscribe to real-time mentions for a user
 *
 * @param options - Hook options
 * @returns Mention data and actions
 *
 * @example
 * ```tsx
 * const { mentions, unreadCount, markAsRead } = useMentions({
 *   userId: currentUserId,
 * });
 * ```
 */
export function useMentions(options: UseMentionsOptions): UseMentionsReturn {
  const { userId, tenantId = getTenantIdSync(), autoSubscribe = true } = options;

  const [mentions, setMentions] = useState<Mention[]>([]);
  const [loading, setLoading] = useState(true);

  // Subscribe to real-time mentions.
  // The service emits unread-then-read (two separate listeners); this sort is
  // what guarantees that contract at the boundary, and orders newest-first
  // within each pile regardless of which listener fired last.
  useEffect(() => {
    if (!userId || !autoSubscribe) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const unsubscribe = subscribeMentions(userId, tenantId, (newMentions) => {
      const sorted = [...newMentions].sort((a, b) => {
        const aUnread = a.status === MentionStatus.UNREAD ? 1 : 0;
        const bUnread = b.status === MentionStatus.UNREAD ? 1 : 0;
        if (aUnread !== bUnread) return bUnread - aUnread;
        return (b.createdAt || '').localeCompare(a.createdAt || '');
      });
      setMentions(sorted);
      setLoading(false);
    });

    return unsubscribe;
  }, [userId, tenantId, autoSubscribe]);

  // The two piles every mention surface renders: "New" on top, "Earlier"
  // (already read) tucked behind a collapsed group.
  const unreadMentions = useMemo(
    () => mentions.filter((m) => m.status === MentionStatus.UNREAD),
    [mentions],
  );
  const readMentions = useMemo(
    () => mentions.filter((m) => m.status === MentionStatus.READ),
    [mentions],
  );

  // Calculate unread count
  const unreadCount = unreadMentions.length;

  // Active count = everything still on the user's plate (unread + read, excludes archived)
  const activeCount = useMemo(() => {
    return mentions.filter((m) => m.status !== MentionStatus.ARCHIVED).length;
  }, [mentions]);

  // Generate notification summary
  const summary = useMemo((): NotificationSummary => {
    const unread = mentions.filter((m) => m.status === MentionStatus.UNREAD);
    const recent = mentions.slice(0, 10);

    const byEntity = {
      deals: unread.filter((m) => m.entityType === 'DEAL').length,
      projects: unread.filter((m) => m.entityType === 'PROJECT').length,
      faults: unread.filter((m) => m.entityType === 'FAULT').length,
      todos: unread.filter((m) => m.entityType === 'TODO').length,
      jobs: unread.filter((m) => m.entityType === 'JOB').length,
      bugFixes: unread.filter((m) => m.entityType === 'BUG_FIX').length,
      vehicles: unread.filter((m) => m.entityType === 'VEHICLE').length,
      timesheets: unread.filter((m) => m.entityType === 'TIMESHEET').length,
    };

    return {
      unreadCount: unread.length,
      recentMentions: recent,
      byEntity,
    };
  }, [mentions]);

  // Mark single mention as read
  const markAsRead = useCallback(async (mentionId: string) => {
    try {
      await markMentionAsRead(mentionId);
      // Optimistic update
      setMentions((prev) =>
        prev.map((m) =>
          m.id === mentionId
            ? { ...m, status: MentionStatus.READ, readAt: new Date().toISOString() }
            : m
        )
      );
    } catch (error) {
      console.error('Error marking mention as read:', error);
    }
  }, []);

  // Cascade — mark every unread mention pointing at one entity as read.
  // Called both when a mention row is opened and when the entity is opened
  // by any other route (see useAutoReadMentions).
  const markEntityAsRead = useCallback(
    async (entityId: string) => {
      if (!userId || !entityId) return;
      const readAt = new Date().toISOString();
      // Optimistic first — the row should demote the instant it's clicked,
      // not a round trip later.
      setMentions((prev) =>
        prev.map((m) =>
          m.entityId === entityId && m.status === MentionStatus.UNREAD
            ? { ...m, status: MentionStatus.READ, readAt }
            : m
        )
      );
      try {
        await markEntityMentionsAsRead(userId, tenantId, entityId);
      } catch (error) {
        // Snapshot listener is the source of truth and will re-assert the
        // real status, so no manual rollback — just don't swallow the cause.
        console.error('Error marking entity mentions as read:', error);
      }
    },
    [userId, tenantId]
  );

  // Opening a mention IS reading it. Cascades to every tag on the same
  // entity, and falls back to the single doc when there's no entity id.
  const openMention = useCallback(
    async (mention: Mention) => {
      if (mention.entityId) {
        await markEntityAsRead(mention.entityId);
      } else if (mention.id) {
        await markAsRead(mention.id);
      }
    },
    [markEntityAsRead, markAsRead]
  );

  // Mark all mentions as read
  const markAllAsRead = async () => {
    if (!userId) return;

    try {
      await markAllMentionsAsRead(userId, tenantId);
      // Optimistic update
      setMentions((prev) =>
        prev.map((m) =>
          m.status === MentionStatus.UNREAD
            ? { ...m, status: MentionStatus.READ, readAt: new Date().toISOString() }
            : m
        )
      );
    } catch (error) {
      console.error('Error marking all mentions as read:', error);
    }
  };

  // Archive a single mention ("clear" / "done").
  // Rethrows on failure so callers can suppress success UI.
  const clearMention = useCallback(async (mentionId: string) => {
    // Snapshot for rollback is captured INSIDE the updater — this callback is
    // memoised, so reading `mentions` from the closure would roll back to a
    // stale list.
    let prevMentions: Mention[] = [];
    // Optimistic: drop from list immediately (snapshot also filters archived)
    setMentions((prev) => {
      prevMentions = prev;
      return prev.filter((m) => m.id !== mentionId);
    });
    try {
      await archiveMention(mentionId);
    } catch (error) {
      console.error('Error archiving mention:', error);
      // Roll back optimistic update
      setMentions(prevMentions);
      throw error;
    }
  }, []);

  // Archive all active mentions. Returns snapshots so caller can offer undo.
  // Rethrows on failure so callers can suppress success UI.
  const clearAllMentions = async () => {
    if (!userId) return [];
    const prevMentions = mentions;
    // Optimistic: snapshot listener only ever returns non-archived, so empty it
    setMentions([]);
    try {
      return await archiveAllMentions(userId, tenantId);
    } catch (error) {
      console.error('Error archiving all mentions:', error);
      setMentions(prevMentions);
      throw error;
    }
  };

  // Archive only what's already been read — the tail end of the cascade.
  // Unread mentions survive. Returns snapshots so caller can offer undo.
  const clearReadMentions = async () => {
    if (!userId) return [];
    let prevMentions: Mention[] = [];
    setMentions((prev) => {
      prevMentions = prev;
      return prev.filter((m) => m.status !== MentionStatus.READ);
    });
    try {
      return await archiveReadMentions(userId, tenantId);
    } catch (error) {
      console.error('Error archiving read mentions:', error);
      setMentions(prevMentions);
      throw error;
    }
  };

  // Restore an archived mention (undo). Defaults to READ since the user has
  // already seen the mention by the time they archive it.
  // Snapshot listener will bring the item back into the list.
  const restoreMention = async (
    mentionId: string,
    priorStatus: MentionStatus = MentionStatus.READ
  ) => {
    try {
      await unarchiveMention(mentionId, priorStatus);
    } catch (error) {
      console.error('Error restoring mention:', error);
      throw error;
    }
  };

  return {
    mentions,
    unreadMentions,
    readMentions,
    unreadCount,
    activeCount,
    loading,
    summary,
    markAsRead,
    markAllAsRead,
    openMention,
    markEntityAsRead,
    clearMention,
    clearAllMentions,
    clearReadMentions,
    restoreMention,
  };
}
